import React, { useMemo, useState, useCallback, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions, ApexAxisChartSeries } from "apexcharts";
import { fixMarkerOverlaps } from "./graphUtils";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AvgGame {
  game_date: string;
  g_match_id: string | null;
  opponent: string | null;
  stat_value: number;
  above_avg: boolean;
}

export interface AvgPlayer {
  player_name: string;
  team_name: string;
  avg_value: number;
  games_count: number;
  dense_rank: number;
  games: AvgGame[];
}

export interface AvgGraphData {
  query_type: string;
  chart_type: string;
  stat_label: string;
  labels: string[];
  values: number[];
  players: AvgPlayer[];
  total_records: number;
  sql_query?: string;
}

interface AvgGraphProps {
  data: AvgGraphData;
  chartReady?: boolean;
  statLabel?: string;
}

// ── Palette ───────────────────────────────────────────────────────────────────

const PALETTE = [
  "#3B82F6", "#10B981", "#F59E0B", "#EC4899",
  "#8B5CF6", "#06B6D4", "#F97316", "#14B8A6", "#EF4444", "#84CC16",
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function formatDateShort(dateStr: string): string {
  if (/^\d{4}$/.test(dateStr.trim())) return dateStr; // season year
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleDateString("en-US", { month: "short" });
  const year = String(d.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
}

function sortedGames(player: AvgPlayer): AvgGame[] {
  return [...player.games].sort(
    (a, b) => new Date(a.game_date).getTime() - new Date(b.game_date).getTime()
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&family=JetBrains+Mono:wght@400;500;600&display=swap');

  .avg * { box-sizing: border-box; }

  .avg {
    --bg: #ffffff;
    --surface: #f8fafc;
    --border: #e8edf3;
    --border-accent: #cbd5e1;
    --text-primary: #0f172a;
    --text-secondary: #475569;
    --text-muted: #94a3b8;
    font-family: 'Plus Jakarta Sans', sans-serif;
    background: var(--bg);
    border-radius: 24px;
    padding: 24px 28px;
    position: relative;
    width: 100%;
    border: 1px solid var(--border);
    box-shadow:
      0 1px 2px rgba(0,0,0,0.04),
      0 4px 16px rgba(0,0,0,0.06),
      0 16px 48px rgba(0,0,0,0.04);
  }

  .avg::before {
    content: '';
    position: absolute;
    top: 0; right: 0;
    width: 260px; height: 160px;
    background: radial-gradient(ellipse at top right, rgba(59,130,246,0.05) 0%, transparent 65%);
    pointer-events: none;
    border-radius: 0 24px 0 0;
  }

  .avg::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, rgba(148,163,184,0.18) 1px, transparent 1px);
    background-size: 28px 28px;
    pointer-events: none;
    border-radius: 24px;
    opacity: 0.55;
    z-index: 0;
  }

  .avg-chips-wrapper { position: relative; z-index: 1; margin-bottom: 18px; }

  .avg-chips {
    display: flex; gap: 6px; flex-wrap: wrap;
    padding: 12px 14px;
    background: var(--surface);
    border-radius: 14px;
    border: 1px solid var(--border); max-height: 164px; overflow-y: auto;
  }

  .avg-chip-all {
    display: flex; align-items: center; gap: 5px;
    padding: 6px 13px; border-radius: 9px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 11px; font-weight: 700;
    border: 1.5px solid var(--border-accent);
    background: white; color: var(--text-secondary);
    cursor: pointer; transition: all 0.18s ease; white-space: nowrap;
  }
  .avg-chip-all:hover { background: #f0f9ff; border-color: #93c5fd; color: #2563eb; }
  .avg-chip-all.active { background: #eff6ff; border-color: #93c5fd; color: #2563eb; }

  .avg-chip {
    display: flex; align-items: center; gap: 7px;
    padding: 5px 10px 5px 8px; border-radius: 9px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 11px; font-weight: 600;
    border: 1.5px solid transparent;
    background: white; color: var(--text-secondary);
    cursor: pointer; transition: all 0.18s cubic-bezier(0.4,0,0.2,1);
    white-space: nowrap; height: 34px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }
  .avg-chip:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.08); color: var(--text-primary); }
  .avg-chip.active {
    border-color: var(--chip-border);
    background: var(--chip-bg);
    color: var(--chip-color);
    box-shadow: 0 2px 8px var(--chip-shadow);
  }

  .avg-swatch {
    width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .avg-chip.active .avg-swatch { transform: scale(1.4); box-shadow: 0 0 6px var(--chip-color); }
  .avg-chip-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 110px; }
  .avg-chip-stat-badge {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px; font-weight: 600;
    padding: 2px 7px; border-radius: 6px;
    background: rgba(0,0,0,0.06); color: var(--text-secondary);
    flex-shrink: 0; transition: background 0.18s ease, color 0.18s ease;
  }
  .avg-chip.active .avg-chip-stat-badge { background: var(--chip-color); color: #ffffff; }
  .avg-chip-check { font-size: 10px; font-weight: 700; flex-shrink: 0; }

  .avg-chart-wrapper {
    position: relative; z-index: 2;
    width: 100%; border-radius: 14px;
    background: white; padding-top: 26px; border: 1px solid var(--border);
  }
  .avg-chart-wrapper .apexcharts-tooltip {
    z-index: 9999 !important; overflow: visible !important;
  }

  .avg-empty {
    height: 100%; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    color: var(--text-muted);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px; font-weight: 600; gap: 8px;
  }
  .avg-empty-icon { font-size: 28px; opacity: 0.35; }

  .avg-legend-row {
    display: flex; align-items: center; gap: 10px;
    padding: 3px 0; font-size: 11px; color: var(--text-secondary);
  }
  .avg-legend-dot {
    width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0;
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

const AvgGraph: React.FC<AvgGraphProps> = ({
  data,
  chartReady = true,
  statLabel = "Stat Value",
}) => {
  const { players } = data;

  // Default: all players selected
  const [selectedPlayers, setSelectedPlayers] = useState<Set<number>>(
    () => new Set(Array.from({ length: Math.min(2, players.length) }, (_, i) => i))
  );

  const togglePlayer = useCallback((idx: number) => {
    setSelectedPlayers((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) { next.delete(idx); } else if (next.size >= 2) { return prev; } else { next.add(idx); }
      return next;
    });
  }, []);

  const selectAllPlayers = useCallback(() => {
    if (selectedPlayers.size >= Math.min(2, players.length)) {
      setSelectedPlayers(new Set());
    } else {
      setSelectedPlayers(new Set(Array.from({ length: Math.min(2, players.length) }, (_, i) => i)));
    }
  }, [players.length, selectedPlayers.size]);

  const selectedIndicesOrdered = useMemo(
    () =>
      Array.from(selectedPlayers)
        .filter((idx) => idx >= 0 && idx < players.length)
        .sort((a, b) => a - b),
    [selectedPlayers, players.length]
  );

  const selectedCount = selectedIndicesOrdered.length;

  // Assign unique colors to selected players (dedupe if same slot)
  const playerColorMap = useMemo(() => {
    const map = new Map<number, string>();
    const used = new Set<number>();
    selectedIndicesOrdered.forEach((playerIdx) => {
      let slot = playerIdx % PALETTE.length;
      while (used.has(slot)) slot = (slot + 1) % PALETTE.length;
      used.add(slot);
      map.set(playerIdx, PALETTE[slot]);
    });
    return map;
  }, [selectedIndicesOrdered]);

  const gameLookup = useMemo<AvgGame[][]>(() => {
    return selectedIndicesOrdered.map((idx) => sortedGames(players[idx]));
  }, [players, selectedIndicesOrdered]);

  const maxGames = useMemo(
    () => Math.max(0, ...gameLookup.map((g) => g.length)),
    [gameLookup]
  );

  // ── x-axis categories ──────────────────────────────────────────────────────
  const xCategories = useMemo(() => {
    if (selectedCount === 1) {
      const g = gameLookup[0];
      if (!g) return [];
      return g.map((game) => formatDateShort(game.game_date));
    }
    return Array.from({ length: maxGames }, (_, i) => `G${i + 1}`);
  }, [selectedCount, gameLookup, maxGames]);

  // ── Series ─────────────────────────────────────────────────────────────────
  const series: ApexAxisChartSeries = useMemo(() => {
    return selectedIndicesOrdered
      .map((playerIdx, seriesIdx) => {
        const player = players[playerIdx];
        if (!player) return null;
        const games = gameLookup[seriesIdx];
        if (!games) return null;

        if (selectedCount === 1) {
          return {
            name: player.player_name,
            data: games.map((g) => g.stat_value),
          };
        }

        return {
          name: player.player_name,
          data: games.map((g, i) => ({
            x: `G${i + 1}`,
            y: g.stat_value,
          })),
        };
      })
      .filter(Boolean) as ApexAxisChartSeries;
  }, [players, selectedIndicesOrdered, selectedCount, gameLookup]);


  // ── Per-player average lines (yaxis annotations) ───────────────────────────

  // ── Horizontal scroll ──────────────────────────────────────────────────────
  const chartWidth = Math.max(700, xCategories.length * 90);
  const chartWrapperRef = useRef<HTMLDivElement>(null);

  // ── ApexCharts options ─────────────────────────────────────────────────────
  const isSinglePlayer = selectedCount === 1;

  const options: ApexOptions = useMemo((): ApexOptions => ({
    chart: {
      type: "line",
      background: "transparent",
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      animations: { enabled: true, speed: 650 },
      height: 380,
      events: {
        mounted: (chartCtx: any) => fixMarkerOverlaps(chartCtx.el),
        updated: (chartCtx: any) => fixMarkerOverlaps(chartCtx.el),
      },
    },
    colors: selectedIndicesOrdered.map((playerIdx) => playerColorMap.get(playerIdx)!),
    stroke: { curve: "smooth", width: 2.5, lineCap: "round" },
    markers: {
      size: 6,
      strokeWidth: 2.5,
      strokeColors: "#ffffff",
      strokeOpacity: 1,
      hover: { size: 6 },
      onClick: (_e: any) => {
        const wrapper = chartWrapperRef.current;
        if (!wrapper) return;
        const point = _e.target;
        const pointX = point.getBoundingClientRect().left;
        const wrapperX = wrapper.getBoundingClientRect().left;
        const offset = pointX - wrapperX;
        wrapper.scrollTo({
          left: wrapper.scrollLeft + offset - wrapper.clientWidth / 2,
          behavior: "smooth",
        });
      },
    },
    xaxis: {
      type: "category" as const,
      categories: xCategories,
      tickPlacement: "on" as const,
      labels: {
        style: {
          colors: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        rotate: isSinglePlayer && xCategories.length > 8 ? -35 : 0,
        rotateAlways: false,
        hideOverlappingLabels: true,
      },
      axisBorder: { show: true, color: "#e8edf3", height: 1 },
      axisTicks: { show: false },
      title: {
        text: isSinglePlayer ? "Game Date" : "Game #",
        style: {
          color: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        offsetY: 6,
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        formatter: (val: number) => val.toFixed(0),
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      title: {
        text: statLabel,
        style: {
          color: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        offsetX: -4,
      },
    },
    grid: {
      show: true,
      borderColor: "#f1f5f9",
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 10, right: 20, bottom: 4, left: 8 },
    },
    legend: { show: false },
    tooltip: {
      theme: "light",
      shared: false,
      intersect: true,
      fixed: { enabled: false },
      followCursor: true,
      custom: ({ seriesIndex, dataPointIndex }) => {
        if (seriesIndex >= selectedIndicesOrdered.length) return "";
        const playerIdx = selectedIndicesOrdered[seriesIndex];
        const player = players[playerIdx];
        if (!player) return "";
        const games = gameLookup[seriesIndex];
        const game = games?.[dataPointIndex];
        if (!game) return "";
        const color = PALETTE[playerIdx % PALETTE.length];
        return buildTooltip(player, game, color, statLabel);
      },
    },
    dataLabels: { enabled: false },
  }), [
    selectedIndicesOrdered, xCategories, statLabel, gameLookup,
    isSinglePlayer, players,
  ]);

  // ── Empty state (called after all hooks per rules-of-hooks) ─────────────────
  if (!players || players.length === 0) {
    return (
      <>
        <style>{css}</style>
        <div className="avg">
          <div className="avg-empty" style={{ height: 360 }}>
            <span className="avg-empty-icon">📭</span>
            <span>No average data available</span>
          </div>
        </div>
      </>
    );
  }

  const allSelected = selectedPlayers.size === Math.min(2, players.length);

  return (
    <>
      <style>{css}</style>
      <div className="avg">

        {selectedPlayers.size >= 2 && (
  <div style={{ position:"relative", zIndex:2, marginBottom:8, fontSize:11, color:"#b45309", fontWeight:600, background:"#fffbeb", border:"1px solid #fde68a", borderRadius:6, padding:"4px 12px", textAlign:"center" }}>
    Maximum 2 players can be compared at once. Deselect a player to add another.
  </div>
)}


        {/* Player chips */}
        <div className="avg-chips-wrapper">
          <div className="avg-chips">
            {players.map((player, idx) => {
              const active = selectedPlayers.has(idx);
              const color = playerColorMap.get(idx) ?? "#cbd5e1";
              return (
                <button
                  key={idx}
                  className={`avg-chip${active ? " active" : ""}`}
                  onClick={() => togglePlayer(idx)}
                  style={{
                    ["--chip-color" as any]: color,
                    ["--chip-bg" as any]: `${color}12`,
                    ["--chip-border" as any]: `${color}40`,
                    ["--chip-shadow" as any]: `${color}18`,
                  }}
                  title={`${player.player_name} · ${player.team_name} · Avg ${fmt(player.avg_value)}`}
                >
                  <span className="avg-swatch" style={{ background: active ? color : "#cbd5e1" }} />
                  <span className="avg-chip-name">{player.player_name}</span>
                  <span className="avg-chip-stat-badge">
                    {fmt(player.avg_value)} · {player.games_count}g
                  </span>
                  {active && <span className="avg-chip-check" style={{ color }}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Chart */}
        <div className="avg-chart-wrapper">

          {/* Avg legend — floating top-right, stays fixed while chart scrolls */}
          {series.length > 0 && chartReady && (
            <div style={{
              position: "absolute", top: 10, right: 14, zIndex: 10,
              display: "flex", gap: 20, flexWrap: "nowrap",
              background: "rgba(255,255,255,0.9)",
              backdropFilter: "blur(4px)",
              borderRadius: 8,
              padding: "6px 14px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            }}>
              {selectedIndicesOrdered.map((playerIdx, i) => {
                const p = players[playerIdx];
                if (!p) return null;
                const color = PALETTE[playerIdx % PALETTE.length];
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
                    <span style={{
                      display: "inline-block", width: 14, height: 0,
                      borderTop: `2px dashed ${color}`,
                      flexShrink: 0,
                    }} />
                    <span style={{ color }}>AVG:</span>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color }}>{fmt(p.avg_value)}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div ref={chartWrapperRef} style={{ overflowX: "auto", overflowY: "hidden" }}>

            {series.length > 0 && chartReady ? (
              <ReactApexChart
                type="line"
                series={series}
                options={options}
                height={380}
                width={chartWidth}
            />
          ) : (
            <div className="avg-empty" style={{ height: 380 }}>
              <span className="avg-empty-icon">↑</span>
              <span>Select players above to view averages</span>
            </div>
          )}
          </div>
        </div>

      </div>
    </>
  );
};

// ── Tooltip builder ───────────────────────────────────────────────────────────

function buildTooltip(
  player: AvgPlayer,
  game: AvgGame,
  color: string,
  statLabel: string
): string {
  const displayDate = formatDateShort(game.game_date);

  return `
    <div style="
      background:#ffffff;
      border:1px solid #e2e8f0;
      border-top:3px solid ${color};
      border-radius:14px;
      padding:13px 15px;
      font-family:'Plus Jakarta Sans',sans-serif;
      box-shadow:0 8px 30px rgba(0,0,0,0.12),0 2px 8px rgba(0,0,0,0.06);
      min-width:205px;max-width:260px;
      position:relative;z-index:9999;
    ">
      <!-- Header -->
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
        <div style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;"></div>
        <span style="font-weight:700;color:#0f172a;font-size:13px;letter-spacing:-0.01em;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
          ${player.player_name}
        </span>
      </div>

      <!-- Date + Opponent -->
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:10px;">
        <div style="background:#f8fafc;border-radius:8px;padding:7px 9px;border:1px solid #f1f5f9;">
          <div style="font-family:'JetBrains Mono',monospace;font-size:8px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:3px;">Date</div>
          <div style="font-size:11px;font-weight:600;color:#475569;">${displayDate}</div>
        </div>
        <div style="background:#f8fafc;border-radius:8px;padding:7px 9px;border:1px solid #f1f5f9;">
          <div style="font-family:'JetBrains Mono',monospace;font-size:8px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:3px;">vs</div>
          <div style="font-size:11px;font-weight:600;color:#475569;">${game.opponent || "N/A"}</div>
        </div>
      </div>

      <!-- Stat value -->
      <div style="
        display:flex;align-items:center;justify-content:space-between;
        background:${color}10;border:1px solid ${color}25;
        border-radius:10px;padding:9px 13px;margin-bottom:8px;
      ">
        <span style="font-family:'JetBrains Mono',monospace;font-size:9px;color:#64748b;text-transform:uppercase;letter-spacing:0.08em;">
          ${statLabel}
        </span>
        <span style="font-size:22px;font-weight:800;color:${color};letter-spacing:-0.02em;">
          ${game.stat_value}
        </span>
      </div>

      <!-- Above / Below avg badge -->
      <div style="
        display:flex;align-items:center;gap:6px;
        background:${game.above_avg ? "#ecfdf5" : "#fef2f2"};
        border-radius:8px;padding:7px 10px;
        border:1px solid ${game.above_avg ? "#6ee7b7" : "#fecaca"};
      ">
        <span style="font-size:14px;">${game.above_avg ? "▲" : "▼"}</span>
        <span style="font-size:10px;font-weight:600;color:${game.above_avg ? "#065f46" : "#991b1b"};">
          ${game.above_avg ? "Above average" : "Below average"} (avg ${fmt(player.avg_value)})
        </span>
      </div>
    </div>`;
}

export default AvgGraph;
